import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Per-row "Manage" menu: a gear button that opens a dropdown of clearly-named
// actions. The menu is portalled to <body> at a fixed position so it is never clipped
// by the table's scroll container. `items` = [{ label, icon, onClick, danger, hidden }].
export function RowMenu({ items, title = 'Manage', emptyLabel = 'No actions available' }) {
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const visible = items.filter((i) => !i.hidden);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  // Position the portalled menu. It is `fixed`, so it is measured against the
  // viewport and will be cut off at the bottom edge unless we flip it above the
  // button — which is exactly what happens for the last row of a long table.
  const MENU_WIDTH_PX = 184;
  const ITEM_HEIGHT_PX = 36;   // px-3 py-2 + text-sm
  const MENU_PADDING_PX = 8;   // the wrapper's p-1, top + bottom
  const GAP_PX = 6;
  const MARGIN_PX = 8;         // don't sit flush against the viewport edge

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const height = Math.max(visible.length, 1) * ITEM_HEIGHT_PX + MENU_PADDING_PX;

      // Flip up when there isn't room below AND there is room above; otherwise
      // stay below and clamp, so a menu taller than the viewport still shows
      // from its top rather than disappearing entirely.
      const spaceBelow = window.innerHeight - r.bottom - GAP_PX - MARGIN_PX;
      const spaceAbove = r.top - GAP_PX - MARGIN_PX;
      const flipUp = spaceBelow < height && spaceAbove > spaceBelow;

      const top = flipUp
        ? Math.max(MARGIN_PX, r.top - GAP_PX - height)
        : Math.min(r.bottom + GAP_PX, Math.max(MARGIN_PX, window.innerHeight - height - MARGIN_PX));

      // Keep it inside the viewport horizontally too (narrow windows / RTL).
      const left = Math.min(
        Math.max(MARGIN_PX, r.right - MENU_WIDTH_PX),
        Math.max(MARGIN_PX, window.innerWidth - MENU_WIDTH_PX - MARGIN_PX),
      );

      setPos({ top, left });
    }
    setOpen((v) => !v);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        title={title}
        aria-label={title}
        className={`rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 dark:border-white/15 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-slate-200 ${open ? 'bg-slate-50 text-slate-700 dark:bg-white/10 dark:text-slate-200' : ''}`}
      >
        <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c-.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
          <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left }}
          className="z-[60] w-46 min-w-[11.5rem] max-h-[calc(100vh-1rem)] overflow-y-auto rounded-xl bg-white p-1 shadow-xl ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-white/10"
        >
          {visible.length === 0 ? (
            <div className="flex items-start gap-2.5 px-3 py-2 text-sm text-slate-400 dark:text-slate-500">
              <svg className="mt-0.5 h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                <rect x="4.5" y="9" width="11" height="7.5" rx="1.5" />
                <path d="M7 9V6.5a3 3 0 016 0V9" strokeLinecap="round" />
              </svg>
              <span>{emptyLabel}</span>
            </div>
          ) : visible.map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={() => { setOpen(false); item.onClick(); }}
              className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm ${item.danger
                ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/15'
                : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-white/5'}`}
            >
              <span className={item.danger ? 'text-red-500 dark:text-red-400' : 'text-slate-400'}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
